import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import {
  MagnifyingGlassIcon, ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon,
  FunnelIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import Avatar from '../components/Avatar';
import styles from './MembersPage.module.css';

const STATUSES = ['', 'active', 'expired', 'pending', 'inactive'];
const STATUS_LABELS = { '': 'All Status', active:'Active', expired:'Expired', pending:'Pending', inactive:'Inactive' };

function daysLeft(expiryDate) {
  if (!expiryDate) return null;
  const d = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
  return d;
}

function DaysLeftBadge({ d }) {
  if (d === null) return <span style={{ color:'var(--text-subtle)' }}>—</span>;
  if (d < 0)  return <span className="badge badge-inactive">Expired</span>;
  if (d <= 7)  return <span className="badge badge-danger">{d}d left</span>;
  if (d <= 30) return <span className="badge badge-warning">{d}d left</span>;
  return <span style={{ color:'var(--text-muted)', fontSize:12 }}>{d}d</span>;
}

// Business Central sync indicator:
//   synced   → green check  (confirmed VIP in BC)
//   not      → amber flag   (member exists but not confirmed in BC)
//   —        → no membership
function BcSyncBadge({ membership }) {
  if (!membership) return <span style={{ color:'var(--text-subtle)' }}>—</span>;
  if (membership.bcSynced) {
    return (
      <span title="Confirmed in Business Central"
        style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#16A34A', fontSize:12, fontWeight:600 }}>
        <CheckBadgeIcon style={{ width:16, height:16 }} /> In BC
      </span>
    );
  }
  return (
    <span title="Not confirmed in Business Central"
      style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#D97706', fontSize:12, fontWeight:600 }}>
      <ExclamationTriangleIcon style={{ width:15, height:15 }} /> Not synced
    </span>
  );
}

export default function MembersPage() {
  const navigate = useNavigate();
  const [members,    setMembers]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [loading,    setLoading]    = useState(true);
  const debounceRef = useRef(null);

  const load = useCallback((p = page, s = search, st = status) => {
    setLoading(true);
    adminAPI.getMembers({ page: p, limit: 20, search: s || undefined, status: st || undefined })
      .then(res => {
        setMembers(res.members || []);
        setTotal(res.total || 0);
        setTotalPages(res.totalPages || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1, search, status); }, [status]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(1, val, status); }, 320);
  };

  const handlePage = (p) => { setPage(p); load(p, search, status); };

  const handleExport = async () => {
    try {
      const blob = await adminAPI.exportMembers();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'members.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + e.message); }
  };

  // Pagination pages list
  const pages = [];
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div>
      <div className="page-header">
        <div><h1>Members</h1><p>{total.toLocaleString()} total members</p></div>
        <div className="page-header-actions">
          <button className="btn-ghost" onClick={handleExport}>
            <ArrowDownTrayIcon style={{ width:15, height:15 }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <MagnifyingGlassIcon style={{ width:15, height:15, color:'var(--text-muted)' }} />
          <input
            className={styles.searchInput}
            placeholder="Search by name, mobile, or member code…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterRight}>
          <FunnelIcon style={{ width:14, height:14, color:'var(--text-muted)' }} />
          {STATUSES.map(s => (
            <button
              key={s}
              className={`${styles.statusTab} ${status === s ? styles.statusTabActive : ''}`}
              onClick={() => { setStatus(s); setPage(1); }}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Mobile</th>
                <th>Member Code</th>
                <th>Status</th>
                <th>BC Sync</th>
                <th>Expiry</th>
                <th>Days Left</th>
                <th>Renewals</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({length:8}).map((_,i) => (
                  <tr key={i}>
                    {[200,120,130,80,90,100,70,60,50].map((w,j) => (
                      <td key={j}><div className="skeleton" style={{ height:16, width:w, borderRadius:6 }} /></td>
                    ))}
                  </tr>
                ))
              ) : members.length === 0 ? (
                <tr><td colSpan={9}>
                  <div className="empty-state">
                    <MagnifyingGlassIcon style={{ width:28, height:28 }} />
                    <h3>No members found</h3>
                    <p>Try adjusting your search or filters.</p>
                  </div>
                </td></tr>
              ) : members.map(m => {
                const dl = m.membership ? daysLeft(m.membership.expiryDate) : null;
                return (
                  <tr key={m.id} className={styles.row} onClick={() => navigate(`/members/${m.id}`)}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Avatar firstName={m.firstName} lastName={m.lastName} size={36} />
                        <div>
                          <div style={{ fontWeight:600, fontSize:13 }}>{m.firstName} {m.lastName}</div>
                          {m.email && <div style={{ fontSize:11, color:'var(--text-muted)' }}>{m.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily:'monospace', fontSize:12 }}>{m.mobile}</td>
                    <td>
                      {m.membership?.memberCode
                        ? <span className={styles.code}>{m.membership.memberCode}</span>
                        : <span style={{ color:'var(--text-subtle)' }}>—</span>}
                    </td>
                    <td>
                      {m.membership?.status
                        ? <span className={`badge badge-${m.membership.status}`}>{m.membership.status}</span>
                        : <span className="badge badge-inactive">No membership</span>}
                    </td>
                    <td><BcSyncBadge membership={m.membership} /></td>
                    <td style={{ fontSize:12, color:'var(--text-muted)' }}>
                      {m.membership?.expiryDate ? format(new Date(m.membership.expiryDate), 'dd MMM yyyy') : '—'}
                    </td>
                    <td><DaysLeftBadge d={dl} /></td>
                    <td style={{ fontSize:12, color:'var(--text-muted)' }}>{m.membership?.renewalCount ?? '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-icon" onClick={() => navigate(`/members/${m.id}`)}>
                        <EyeIcon style={{ width:15, height:15 }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>
              Page {page} of {totalPages} · {total.toLocaleString()} members
            </span>
            <div className={styles.pages}>
              <button className={styles.pageBtn} disabled={page===1} onClick={() => handlePage(page-1)}>
                <ChevronLeftIcon style={{ width:14, height:14 }} />
              </button>
              {start > 1 && <><button className={styles.pageBtn} onClick={() => handlePage(1)}>1</button><span className={styles.pageEllipsis}>…</span></>}
              {pages.map(p => (
                <button key={p} className={`${styles.pageBtn} ${p===page ? styles.pageBtnActive : ''}`} onClick={() => handlePage(p)}>{p}</button>
              ))}
              {end < totalPages && <><span className={styles.pageEllipsis}>…</span><button className={styles.pageBtn} onClick={() => handlePage(totalPages)}>{totalPages}</button></>}
              <button className={styles.pageBtn} disabled={page===totalPages} onClick={() => handlePage(page+1)}>
                <ChevronRightIcon style={{ width:14, height:14 }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
